import { Alert, Button, Card, CardBody, Input, Spinner } from '@foundation/ui'
import { createFileRoute, redirect } from '@tanstack/react-router'
import { useCallback, useEffect, useState } from 'react'
import styles from './alerts.module.css'
import { AppLayout } from '@/components/AppLayout'
import { api, type AlertChannel } from '@/lib/api'
import { isAuthenticated } from '@/lib/auth-store'
import { toast } from '@/lib/toast'

export const Route = createFileRoute('/alerts')({
  beforeLoad: () => {
    if (!isAuthenticated()) {
      throw redirect({ to: '/login' })
    }
  },
  component: AlertsPage,
})

interface ChannelState {
  enabled: boolean
  config: Record<string, unknown>
  isDirty: boolean
  isSaving: boolean
  isTesting: boolean
}

type ChannelStates = Record<AlertChannel, ChannelState>

const defaultChannelStates: ChannelStates = {
  email: {
    enabled: false,
    config: { emails: '' },
    isDirty: false,
    isSaving: false,
    isTesting: false,
  },
  slack: {
    enabled: false,
    config: { webhookUrl: '' },
    isDirty: false,
    isSaving: false,
    isTesting: false,
  },
  webhook: {
    enabled: false,
    config: { url: '', secret: '' },
    isDirty: false,
    isSaving: false,
    isTesting: false,
  },
}

const channelMeta: { channel: AlertChannel; label: string; icon: string; description: string }[] = [
  {
    channel: 'email',
    label: 'Email Alerts',
    icon: '📧',
    description:
      'Receive pricing change alerts via email. Add multiple addresses separated by commas.',
  },
  {
    channel: 'slack',
    label: 'Slack Alerts',
    icon: '💬',
    description: 'Send alerts to a Slack channel via incoming webhook.',
  },
  {
    channel: 'webhook',
    label: 'Webhook Alerts',
    icon: '🔗',
    description: 'Send JSON payloads to your custom endpoint when changes are detected.',
  },
]

function AlertsPage() {
  const [channels, setChannels] = useState<ChannelStates>(defaultChannelStates)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Global alert settings (local state, persisted with email channel or separately)
  const [alertOnIncrease, setAlertOnIncrease] = useState(true)
  const [alertOnDecrease, setAlertOnDecrease] = useState(true)
  const [minThreshold, setMinThreshold] = useState('0')

  const fetchConfigs = useCallback(async () => {
    try {
      setIsLoading(true)
      setError(null)
      const result = await api.listAlertConfigs()
      const configs = result.data ?? []

      const newStates = { ...defaultChannelStates }
      for (const config of configs) {
        const ch = config.channelType
        if (ch in newStates) {
          newStates[ch] = {
            enabled: config.enabled,
            config: { ...newStates[ch].config, ...config.config },
            isDirty: false,
            isSaving: false,
            isTesting: false,
          }
        }
      }
      setChannels(newStates)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load alert configurations'
      setError(message)
      toast.error(message)
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    void fetchConfigs()
  }, [fetchConfigs])

  const updateChannel = (channel: AlertChannel, updates: Partial<ChannelState>) => {
    setChannels((prev) => ({
      ...prev,
      [channel]: { ...prev[channel], ...updates, isDirty: true },
    }))
  }

  const updateChannelConfig = (channel: AlertChannel, key: string, value: unknown) => {
    setChannels((prev) => ({
      ...prev,
      [channel]: {
        ...prev[channel],
        config: { ...prev[channel].config, [key]: value },
        isDirty: true,
      },
    }))
  }

  const handleSave = async (channel: AlertChannel) => {
    const state = channels[channel]
    try {
      setChannels((prev) => ({
        ...prev,
        [channel]: { ...prev[channel], isSaving: true },
      }))
      await api.upsertAlertConfig(channel, {
        enabled: state.enabled,
        config: state.config,
      })
      setChannels((prev) => ({
        ...prev,
        [channel]: { ...prev[channel], isDirty: false, isSaving: false },
      }))
      toast.success(`${channel} alert configuration saved`)
    } catch (err) {
      setChannels((prev) => ({
        ...prev,
        [channel]: { ...prev[channel], isSaving: false },
      }))
      toast.error(err instanceof Error ? err.message : `Failed to save ${channel} configuration`)
    }
  }

  const handleTest = async (channel: AlertChannel) => {
    try {
      setChannels((prev) => ({
        ...prev,
        [channel]: { ...prev[channel], isTesting: true },
      }))
      await api.testAlertConfig(channel)
      toast.success(`Test ${channel} alert sent successfully`)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : `Failed to send test ${channel} alert`)
    } finally {
      setChannels((prev) => ({
        ...prev,
        [channel]: { ...prev[channel], isTesting: false },
      }))
    }
  }

  if (isLoading) {
    return (
      <AppLayout title="Alerts">
        <div className={styles.loadingContainer}>
          <Spinner size="lg" />
          <span>Loading alert configurations...</span>
        </div>
      </AppLayout>
    )
  }

  if (error && !Object.values(channels).some((c) => c.enabled)) {
    return (
      <AppLayout title="Alerts">
        <Alert variant="danger">{error}</Alert>
        <div style={{ marginTop: '1rem' }}>
          <Button onClick={() => void fetchConfigs()}>Retry</Button>
        </div>
      </AppLayout>
    )
  }

  const hasEnabledChannels = Object.values(channels).some((c) => c.enabled)

  return (
    <AppLayout title="Alerts">
      {/* Empty state when no channels are enabled */}
      {!hasEnabledChannels && (
        <Card className={styles.emptyCard ?? ''}>
          <CardBody>
            <div className={styles.emptyState ?? ''}>
              <div className={styles.emptyIllustration ?? ''}>
                <svg
                  width="120"
                  height="120"
                  viewBox="0 0 120 120"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M60 25 L60 15"
                    stroke="var(--color-warning)"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                  />
                  <path
                    d="M40 30 L35 22"
                    stroke="var(--color-warning)"
                    strokeWidth="2"
                    strokeLinecap="round"
                    opacity="0.5"
                  />
                  <path
                    d="M80 30 L85 22"
                    stroke="var(--color-warning)"
                    strokeWidth="2"
                    strokeLinecap="round"
                    opacity="0.5"
                  />
                  <path
                    d="M35 55 C35 41 46 30 60 30 C74 30 85 41 85 55 L85 72 L92 82 L28 82 L35 72 Z"
                    stroke="var(--text-muted)"
                    strokeWidth="2"
                    fill="none"
                  />
                  <rect
                    x="50"
                    y="82"
                    width="20"
                    height="6"
                    rx="3"
                    stroke="var(--text-muted)"
                    strokeWidth="2"
                    fill="none"
                  />
                  <circle cx="60" cy="55" r="4" fill="var(--color-primary)" opacity="0.3" />
                  <circle
                    cx="60"
                    cy="55"
                    r="8"
                    stroke="var(--color-primary)"
                    strokeWidth="1.5"
                    fill="none"
                    opacity="0.2"
                  />
                  <circle
                    cx="60"
                    cy="55"
                    r="14"
                    stroke="var(--color-primary)"
                    strokeWidth="1"
                    fill="none"
                    opacity="0.1"
                  />
                </svg>
              </div>
              <h3 className={styles.emptyTitle ?? ''}>Set up your first alert</h3>
              <p className={styles.emptyDescription ?? ''}>
                Enable a notification channel below to start receiving alerts when competitor
                pricing changes are detected.
              </p>
            </div>
          </CardBody>
        </Card>
      )}

      {/* Alert Channels */}
      <div className={styles.channelGrid}>
        {channelMeta.map(({ channel, label, icon, description }) => {
          const state = channels[channel]
          return (
            <Card
              key={channel}
              className={`${styles.channelCard} ${!state.enabled ? styles.channelCardDisabled : ''}`}
            >
              <CardBody>
                {/* Channel Header with Toggle */}
                <div className={styles.channelHeader}>
                  <div className={styles.channelInfo}>
                    <span className={styles.channelIcon}>{icon}</span>
                    <span className={styles.channelName}>{label}</span>
                  </div>
                  <div className={styles.channelToggle}>
                    <label className={styles.toggleLabel}>
                      <input
                        type="checkbox"
                        checked={state.enabled}
                        onChange={(e) => updateChannel(channel, { enabled: e.target.checked })}
                      />
                      <span className={styles.toggleSwitch} />
                    </label>
                  </div>
                </div>

                <p className={styles.channelDescription}>{description}</p>

                {/* Channel-specific config */}
                <div className={styles.channelForm}>
                  {channel === 'email' && (
                    <Input
                      label="Email Addresses"
                      type="text"
                      placeholder="you@example.com, team@example.com"
                      value={String(state.config.emails ?? '')}
                      onChange={(e) => updateChannelConfig(channel, 'emails', e.target.value)}
                      disabled={!state.enabled}
                    />
                  )}

                  {channel === 'slack' && (
                    <Input
                      label="Webhook URL"
                      type="url"
                      placeholder="https://hooks.slack.com/services/..."
                      value={String(state.config.webhookUrl ?? '')}
                      onChange={(e) => updateChannelConfig(channel, 'webhookUrl', e.target.value)}
                      disabled={!state.enabled}
                    />
                  )}

                  {channel === 'webhook' && (
                    <>
                      <Input
                        label="Endpoint URL"
                        type="url"
                        placeholder="https://api.example.com/webhooks/pricespy"
                        value={String(state.config.url ?? '')}
                        onChange={(e) => updateChannelConfig(channel, 'url', e.target.value)}
                        disabled={!state.enabled}
                      />
                      <Input
                        label="Secret (optional)"
                        type="password"
                        placeholder="Signing secret for payload verification"
                        value={String(state.config.secret ?? '')}
                        onChange={(e) => updateChannelConfig(channel, 'secret', e.target.value)}
                        disabled={!state.enabled}
                      />
                    </>
                  )}
                </div>

                {/* Channel Actions */}
                <div className={styles.channelActions}>
                  <Button
                    variant="primary"
                    size="sm"
                    loading={state.isSaving}
                    disabled={!state.isDirty}
                    onClick={() => void handleSave(channel)}
                  >
                    Save
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    loading={state.isTesting}
                    disabled={!state.enabled}
                    onClick={() => void handleTest(channel)}
                  >
                    Send Test
                  </Button>
                </div>
              </CardBody>
            </Card>
          )
        })}
      </div>

      {/* Global Alert Settings */}
      <div className={styles.globalSection}>
        <h2 className={styles.sectionTitle}>Alert Preferences</h2>
        <p className={styles.sectionDescription}>
          Configure which types of pricing changes trigger alerts.
        </p>

        <Card>
          <CardBody>
            <div className={styles.settingsForm}>
              <div className={styles.settingRow}>
                <div className={styles.settingToggle}>
                  <label>
                    <input
                      type="checkbox"
                      checked={alertOnIncrease}
                      onChange={(e) => setAlertOnIncrease(e.target.checked)}
                    />
                    <span className={styles.toggleSwitch} />
                    <div className={styles.settingText}>
                      <span className={styles.settingTitle}>Alert on price increase</span>
                      <span className={styles.settingHint}>
                        Notify when a competitor raises their prices
                      </span>
                    </div>
                  </label>
                </div>
              </div>

              <div className={styles.settingRow}>
                <div className={styles.settingToggle}>
                  <label>
                    <input
                      type="checkbox"
                      checked={alertOnDecrease}
                      onChange={(e) => setAlertOnDecrease(e.target.checked)}
                    />
                    <span className={styles.toggleSwitch} />
                    <div className={styles.settingText}>
                      <span className={styles.settingTitle}>Alert on price decrease</span>
                      <span className={styles.settingHint}>
                        Notify when a competitor lowers their prices
                      </span>
                    </div>
                  </label>
                </div>
              </div>

              <div className={styles.settingRow}>
                <div className={styles.thresholdRow}>
                  <span className={styles.thresholdLabel}>Minimum change threshold:</span>
                  <Input
                    className={styles.thresholdInput}
                    type="number"
                    min="0"
                    step="1"
                    value={minThreshold}
                    onChange={(e) => setMinThreshold(e.target.value)}
                  />
                  <span className={styles.thresholdLabel}>%</span>
                </div>
              </div>
            </div>
          </CardBody>
        </Card>
      </div>
    </AppLayout>
  )
}
